import { Badge } from "@/components/ui/badge";
import api from "@/lib/axios";
import { DataNilaiRaportSmbjm } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { CheckCircle, Clock, Users, ArrowRight } from "lucide-react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { columns } from "./partial/Columns";
import { DataTable } from "./partial/DataTable";

const getListDataNilaiRaportSmbjm = async (
  limit?: string,
  page?: string,
  status_verifikasi?: string,
  search?: string,
) => {
  const params = new URLSearchParams(location.search);

  if (limit) params.set("rowsPerPage", limit);
  if (page) params.set("page", page);
  if (status_verifikasi == "belum-diverifikasi") {
    params.set("belumCek", "true");
  } else if (status_verifikasi == "sudah-diverifikasi") {
    params.set("belumCek", "false");
  } else if (status_verifikasi == "rekon") {
    params.set("isPerluRekon", "true");
  } else if (status_verifikasi == "belum-rekon") {
    params.set("filterRekon", "belum_rekon");
  } else if (status_verifikasi == "sudah-rekon") {
    params.set("filterRekon", "sudah_rekon");
  }
  if (search) params.set("searchQuery", search);

  const response = await api.get(
    `/verifikator/peserta-rapor-smbjm/index?${params.toString()}`,
  );
  return response.data;
};

// Komponen Pembantu: Circular Progress dengan Gradient
const CircularProgress = ({ percentage }: { percentage: number }) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center drop-shadow-sm">
      <svg className="w-full h-full transform -rotate-90">
        <defs>
          <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" /> {/* text-blue-500 */}
            <stop offset="100%" stopColor="#8b5cf6" /> {/* text-indigo-500 */}
          </linearGradient>
          <linearGradient
            id="greenGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#22c55e" /> {/* text-green-500 */}
            <stop offset="100%" stopColor="#10b981" /> {/* text-emerald-500 */}
          </linearGradient>
        </defs>

        {/* Background Circle */}
        <circle
          cx="50%"
          cy="50%"
          r={radius}
          stroke="currentColor"
          strokeWidth="8"
          fill="transparent"
          className="text-gray-100 dark:text-gray-700/50"
        />

        {/* Progress Circle */}
        <circle
          cx="50%"
          cy="50%"
          r={radius}
          stroke={
            percentage === 100 ? "url(#greenGradient)" : "url(#blueGradient)"
          }
          strokeWidth="8"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1500 ease-out"
        />
      </svg>
      {/* Center Text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tighter">
          {percentage.toFixed(0)}
          <span className="text-sm sm:text-base font-medium">%</span>
        </span>
      </div>
    </div>
  );
};

export default function VerifikatorDataNilaiRaportSmbjmPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const limitParams = searchParams.get("rowsPerPage") || "";
  const pageParams = searchParams.get("page") || "";
  const statusVerifikasiParams = searchParams.get("status-verifikasi") || "";
  const searchQueryParams = searchParams.get("search") || "";

  const { data: dataRawAll, isPending: isPendingDataAll } = useQuery({
    queryKey: ["listDataNilaiRaportSmbjm"],
    queryFn: () =>
      getListDataNilaiRaportSmbjm("999", undefined, undefined, undefined),
  });

  const { data: dataRaw, isPending } = useQuery({
    queryKey: [
      "listDataNilaiRaportSmbjm",
      limitParams,
      pageParams,
      statusVerifikasiParams,
      searchQueryParams,
    ],
    queryFn: () =>
      getListDataNilaiRaportSmbjm(
        limitParams,
        pageParams,
        statusVerifikasiParams,
        searchQueryParams,
      ),
  });

  const dataAllList = dataRawAll?.data?.data || [];
  const totalSiswa = dataAllList.length;

  const siswaVerified = dataAllList.filter(
    (student: DataNilaiRaportSmbjm) => student.jml_belum_cek === 0,
  ).length;

  const siswaBelumVerified = dataAllList.filter(
    (student: DataNilaiRaportSmbjm) => Number(student.jml_belum_cek) > 0,
  ).length;

  const validationPercentage =
    totalSiswa > 0 ? (siswaVerified / totalSiswa) * 100 : 0;
  const pendingPercentage =
    totalSiswa > 0 ? (siswaBelumVerified / totalSiswa) * 100 : 0;

  const verifikatorKe = dataRaw?.verifikatorKe || 0;

  useEffect(() => {
    if (verifikatorKe === 3 && !searchParams.has("status-verifikasi")) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("status-verifikasi", "rekon");
          return newParams;
        },
        { replace: true },
      );
    }
  }, [verifikatorKe, searchParams, setSearchParams]);

  // Handler untuk klik Card "Perlu Diperiksa"
  const handleFilterBelumDiverifikasi = () => {
    const params = new URLSearchParams(location.search);
    params.set("status-verifikasi", "belum-diverifikasi");
    params.delete("page"); // Reset ke halaman 1 saat ganti filter
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <div className="text-gray-900 dark:text-gray-100 transition-colors duration-200">
      <div className="space-y-2 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Verifikasi Nilai Raport SMBJM
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Monitoring status verifikasi data pendaftaran siswa
        </p>
      </div>

      {verifikatorKe !== 3 && (
        <>
          {/* Hero Stats Section */}
          <section className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            {/* Main Progress Circular Chart */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-5 sm:p-6 border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between transition-colors duration-200">
              <div className="space-y-3">
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Progress Validasi
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[200px]">
                    Persentase berkas yang telah diperiksa oleh Verifikator.
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className={
                    validationPercentage === 100
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800"
                      : "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-100 dark:border-blue-800"
                  }
                >
                  {validationPercentage === 100 ? "Selesai" : "Sedang Berjalan"}
                </Badge>
              </div>
              <CircularProgress percentage={validationPercentage} />
            </div>

            {/* Total Siswa */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col justify-center transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Siswa
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                    {isPendingDataAll ? "..." : totalSiswa}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <Users className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                </div>
              </div>
            </div>

            {/* Belum Divalidasi - Clickable Card */}
            <button
              onClick={handleFilterBelumDiverifikasi}
              className="text-left w-full bg-white dark:bg-gray-800 rounded-xl p-5 border border-orange-100 dark:border-orange-900/30 shadow-sm hover:shadow-md hover:border-orange-300 dark:hover:border-orange-800/80 transition-all duration-200 group flex flex-col justify-center focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <div className="flex items-center justify-between w-full">
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">
                      Perlu Diperiksa
                    </p>
                    <ArrowRight className="w-3 h-3 text-orange-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                  <p className="text-3xl font-bold text-orange-700 dark:text-orange-300 mt-1 group-hover:scale-105 transform origin-left transition-transform duration-300">
                    {isPendingDataAll ? "..." : siswaBelumVerified}
                  </p>
                  <p className="text-xs text-orange-500/80 dark:text-orange-500/60 mt-1">
                    {pendingPercentage?.toFixed(1)}% dari total
                  </p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl group-hover:bg-orange-100 dark:group-hover:bg-orange-900/40 transition-colors">
                  <Clock className="w-6 h-6 text-orange-500 dark:text-orange-400 group-hover:animate-pulse" />
                </div>
              </div>
            </button>
          </section>

          {/* Rincian Status (Linear Progress Bars for secondary details) */}
          <section className="mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4">
                Rincian Status
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Sudah Divalidasi
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {siswaVerified} siswa
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${validationPercentage}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Belum Divalidasi
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                      {siswaBelumVerified} siswa
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-orange-400 to-amber-500 h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${pendingPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Status Message */}
            <div className="mt-4 p-4 bg-blue-50/80 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30 transition-colors duration-200">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-blue-100 dark:bg-blue-900/40 rounded-full mt-0.5 shadow-sm">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    {validationPercentage == 100
                      ? "Validasi sudah selesai! Semua data telah diperiksa."
                      : validationPercentage >= 80
                        ? "Validasi hampir selesai! Tinggal sedikit lagi."
                        : validationPercentage >= 50
                          ? "Validasi sedang berjalan dengan baik."
                          : "Masih banyak data yang perlu divalidasi. Silakan lanjutkan pengecekan."}
                  </p>
                  {siswaBelumVerified > 0 && (
                    <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1.5">
                      Tersisa <strong>{siswaBelumVerified}</strong> siswa yang
                      perlu divalidasi.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <DataTable
        data={dataRaw?.data?.data || []}
        verifikatorKe={verifikatorKe}
        paginationData={dataRaw?.data || {}}
        columns={columns}
        isLoading={isPending}
      />
    </div>
  );
}
